import { supabase } from '@config/supabase';
import { ROUTES } from '@config/constants';
import type { AuthUser, SignUpData, SignInData } from './types';
import { createUserFromSession, fetchUserProfileFromDB, buildVerificationRedirectUrl } from './utils';
import { handleAuthError } from './errors';
import { mfaService } from '@services/mfa.service';
import { authCache, USER_STATE_STORAGE_KEY, SESSION_HINT_KEY } from './authCache';

export const signUp = async (data: SignUpData, set: any, _get: any) => {
  set({ isLoading: true, error: null });
  console.log('📝 Creating new user...');

  try {
    const redirectUrl = buildVerificationRedirectUrl();
    console.log('📧 Signup with email redirect to:', redirectUrl);

    let result: any = null;
    let error: any = null;

    // Primary path with explicit redirect URL.
    ({ data: result, error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: {
          full_name: data.username || data.email.split('@')[0],
        },
        emailRedirectTo: redirectUrl,
      },
    }));

    // Fallback if redirect URL policy rejects the URL
    if (error?.message?.toLowerCase().includes('redirect') || error?.message?.toLowerCase().includes('redirect_to')) {
      console.warn('⚠️ Signup failed due to redirect URL policy, retrying without emailRedirectTo');
      ({ data: result, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.username || data.email.split('@')[0],
          },
        },
      }));
    }

    if (error) {
      console.error('❌ Signup error:', error.message);
      const authError = handleAuthError(error);
      set({ error: authError.message, isLoading: false });
      throw authError;
    }

    if (!result?.user) {
      throw new Error('No user returned from Supabase');
    }

    console.log('✅ User created:', result.user.email);
    console.log('📧 Email confirmation status:', {
      email_confirmed_at: result.user.email_confirmed_at,
      has_session: !!result.session,
    });

    if (result.user && !result.user.email_confirmed_at) {
      try {
        let delivered = false;

        const { error: customEmailError } = await supabase.functions.invoke('send-signup-email', {
          body: {
            email: data.email,
            user_id: result.user.id,
            password: data.password,
            redirect_to: redirectUrl,
          },
        });

        if (customEmailError) {
          console.error('❌ Custom signup email via Edge Function failed:', customEmailError);
        } else {
          console.log('✅ Signup confirmation email sent via Edge Function');
          delivered = true;
        }

        if (!delivered) {
          const { error: resendError } = await supabase.auth.resend({
            type: 'signup',
            email: data.email,
            options: {
              emailRedirectTo: redirectUrl,
            },
          });
          if (resendError) {
            console.error('❌ Supabase signup resend failed:', resendError);
          } else {
            console.log('✅ Signup confirmation email sent via Supabase resend fallback');
          }
        }
      } catch (emailErr: any) {
        console.error('❌ Error sending signup confirmation email:', emailErr);
      }
    }

    if (!result.session) {
      console.log('📧 Email confirmation required - no session returned');
      set({ 
        user: null, 
        isAuthenticated: false, 
        isLoading: false,
        error: 'EMAIL_CONFIRMATION_REQUIRED'
      });
      return;
    }

    console.log('⚠️ User already confirmed - setting as authenticated');
    const basicUser = createUserFromSession(result.session);
    set({ user: basicUser, isAuthenticated: true, isLoading: false, isSuspended: false });
    
    fetchUserProfileFromDB(result.user.id).then((fullUser: AuthUser | null) => {
      if (fullUser) {
        set({ user: fullUser, isSuspended: fullUser.isSuspended || false });
      }
    });
    
  } catch (error: any) {
    console.error('❌ Signup failed:', error);
    const authError = handleAuthError(error);
    set({ error: authError.message, isLoading: false });
    throw authError;
  }
};

export const signIn = async (data: SignInData, set: any, _get: any) => {
  set({ isLoading: true, error: null });
  console.log('📧 Attempting login...');

  try {
    const { data: result, error } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });

    if (error) {
      const authError = handleAuthError(error);
      set({ error: authError.message, isLoading: false });
      throw authError;
    }

    if (!result.user) {
      throw new Error('No user data returned');
    }

    const PROFILE_FETCH_TIMEOUT_MS = 12_000;
    let fullUser: AuthUser | null = null;
    try {
      fullUser = await Promise.race([
        fetchUserProfileFromDB(result.user.id),
        new Promise<null>((_, reject) =>
          setTimeout(() => reject(new Error('PROFILE_FETCH_TIMEOUT')), PROFILE_FETCH_TIMEOUT_MS)
        ),
      ]);
    } catch (profileError: any) {
      if (profileError?.message === 'PROFILE_FETCH_TIMEOUT') {
        console.warn('⚠️ Profile fetch timed out – continuing with session data');
        const basicUser = createUserFromSession(result.session!);
        set({
          user: basicUser,
          isAuthenticated: true,
          isLoading: false,
          session: result.session,
          error: null,
          isSuspended: false,
        });
        fetchUserProfileFromDB(result.user.id).then((profile) => {
          if (profile) set({ user: profile, isSuspended: profile.isSuspended ?? false });
        }).catch(() => {});
        return;
      }
      if (profileError.message === 'ACCOUNT_DELETED' || profileError.message?.includes('ACCOUNT_DELETED')) {
        console.warn('⚠️ Account was deleted - signing out');
        authCache.sessionCache = { session: null, timestamp: Date.now() };
        await supabase.auth.signOut();
        const authError = handleAuthError(new Error('ACCOUNT_DELETED'));
        set({ 
          user: null, 
          isAuthenticated: false, 
          isLoading: false,
          session: null,
          error: authError.message,
          isSuspended: false
        });
        throw authError;
      }
      throw profileError;
    }
    
    if (!fullUser) {
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser(result.user.id);
      if (authError || !authUser) {
        console.warn('⚠️ Account was deleted - signing out');
        authCache.sessionCache = { session: null, timestamp: Date.now() };
        await supabase.auth.signOut();
        const authErrorObj = handleAuthError(new Error('ACCOUNT_DELETED'));
        set({ 
          user: null, 
          isAuthenticated: false, 
          isLoading: false,
          session: null,
          error: authErrorObj.message,
          isSuspended: false
        });
        throw authErrorObj;
      }
      console.warn('⚠️ Account was hard-deleted (profile removed) - signing out');
      authCache.sessionCache = { session: null, timestamp: Date.now() };
      await supabase.auth.signOut();
      const authErrorObj = handleAuthError(new Error('ACCOUNT_DELETED'));
      set({ 
        user: null, 
        isAuthenticated: false, 
        isLoading: false,
        session: null,
        error: authErrorObj.message,
        isSuspended: false
      });
      throw authErrorObj;
    }
    
    if (fullUser.isSuspended) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('suspension_download_enabled, suspension_download_expires_at, suspension_started_at, suspension_grace_period_days')
        .eq('id', result.user.id)
        .single();
      
      const hasDownloadAccess = profile?.suspension_download_enabled === true;
      
      if (hasDownloadAccess) {
        console.warn('⚠️ Account is suspended but download access is enabled');
        set({ 
          user: fullUser, 
          isAuthenticated: true, 
          isLoading: false,
          session: result.session, 
          isSuspended: true,
          error: null
        });
        
        import('@utils/sentry').then(({ setUser: setSentryUser }) => {
          setSentryUser({ id: fullUser.id, email: fullUser.email, username: fullUser.username });
        }).catch(() => {});
        
        return;
      }
      
      console.warn('⚠️ Account is suspended - no download access');
      
      import('@utils/sentry').then(({ captureException, setUser: setSentryUser }) => {
        setSentryUser({ id: result.user.id, email: result.user.email || data.email, username: fullUser!.username });
        const error = new Error('Suspended account attempted login');
        error.name = 'SuspendedAccountLoginAttempt';
        captureException(error, {
          source: 'auth',
          userId: result.user.id,
          userEmail: result.user.email || data.email,
          hasDownloadAccess: false,
          suspensionStartedAt: profile?.suspension_started_at,
          suspensionGracePeriodDays: profile?.suspension_grace_period_days,
        });
      }).catch(() => {});
      
      authCache.sessionCache = { session: null, timestamp: Date.now() };
      await supabase.auth.signOut();
      
      set({ 
        user: null, 
        isAuthenticated: false, 
        isLoading: false,
        session: null,
        error: 'ACCOUNT_SUSPENDED',
        isSuspended: true
      });
      
      import('@utils/sentry').then(({ clearUser }) => {
        clearUser();
      }).catch(() => {});
      
      throw new Error('ACCOUNT_SUSPENDED');
    }

    if (fullUser.mfaEnabled && result.session?.access_token) {
      authCache.pendingMfaAccessToken = result.session.access_token;
      try {
        await mfaService.requestCode(result.session.access_token);
      } catch (mfaErr: any) {
        console.warn('MFA request failed:', mfaErr);
        authCache.pendingMfaAccessToken = null;
        const authError = handleAuthError(mfaErr);
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          session: null,
          error: authError.message,
          pendingMfaEmail: data.email,
          isSuspended: false,
        });
        authCache.sessionCache = { session: null, timestamp: Date.now() };
        await supabase.auth.signOut();
        return;
      }
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        session: null,
        error: null,
        pendingMfaEmail: data.email,
        isSuspended: false,
      });
      authCache.sessionCache = { session: null, timestamp: Date.now() };
      await supabase.auth.signOut();
      return;
    }
    
    set({ user: fullUser, isAuthenticated: true, isLoading: false, session: result.session, isSuspended: false });
    console.log('✅ Login successful:', fullUser.email);
    
    import('@utils/sentry').then(({ setUser: setSentryUser }) => {
      setSentryUser({ id: fullUser!.id, email: fullUser!.email, username: fullUser!.username });
    }).catch(() => {});
    
  } catch (error: any) {
    console.error('❌ Login failed:', error);
    const authError = handleAuthError(error);
    set({ error: authError.message, isLoading: false });
    throw authError;
  }
};

export const completeMfaLogin = async (email: string, code: string, set: any, _get: any) => {
  set({ isLoading: true, error: null });
  try {
    const { token } = await mfaService.verifyCode(email, code);
    const { data: otpData, error: otpError } = await supabase.auth.verifyOtp({
      token_hash: token,
      type: 'magiclink',
    });
    if (otpError || !otpData?.user) {
      const authError = handleAuthError(otpError || new Error('Verification failed'));
      set({ error: authError.message, isLoading: false });
      throw authError;
    }
    const fullUser = await fetchUserProfileFromDB(otpData.user.id);
    if (!fullUser) {
      authCache.pendingMfaAccessToken = null;
      await supabase.auth.signOut();
      const authError = handleAuthError(new Error('ACCOUNT_DELETED'));
      set({ user: null, isAuthenticated: false, isLoading: false, session: null, pendingMfaEmail: null, error: authError.message, isSuspended: false });
      throw authError;
    }
    if (fullUser.isSuspended) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('suspension_download_enabled')
        .eq('id', otpData.user.id)
        .single();
      if (profile?.suspension_download_enabled !== true) {
        authCache.pendingMfaAccessToken = null;
        await supabase.auth.signOut();
        set({ user: null, isAuthenticated: false, isLoading: false, session: null, pendingMfaEmail: null, error: 'ACCOUNT_SUSPENDED', isSuspended: true });
        throw new Error('ACCOUNT_SUSPENDED');
      }
    }
    authCache.pendingMfaAccessToken = null;
    authCache.sessionCache = { session: otpData.session, timestamp: Date.now() };
    set({
      user: fullUser,
      isAuthenticated: true,
      isLoading: false,
      session: otpData.session,
      pendingMfaEmail: null,
      error: null,
      isSuspended: fullUser.isSuspended ?? false,
    });
    import('@utils/sentry').then(({ setUser: setSentryUser }) => {
      setSentryUser({ id: fullUser.id, email: fullUser.email, username: fullUser.username });
    }).catch(() => {});
  } catch (err: any) {
    const authError = handleAuthError(err);
    set({ error: authError.message, isLoading: false });
    throw authError;
  }
};

export const requestMfaCodeAgain = async (set: any) => {
  const token = authCache.pendingMfaAccessToken;
  if (!token) return;
  try {
    await mfaService.requestCode(token);
  } catch (e: any) {
    const authError = handleAuthError(e);
    set({ error: authError.message });
    throw authError;
  }
};

export const signOut = async (set: any) => {
  try {
    console.log('🚪 Logging out...');
    await supabase.auth.signOut();
    if (typeof window !== 'undefined') try { sessionStorage.removeItem(SESSION_HINT_KEY); } catch (_) {}
    authCache.pendingMfaAccessToken = null;
    console.log('✅ Logged out successfully');
  } catch (error) {
    console.error('❌ Logout error:', error);
    if (typeof window !== 'undefined') try { sessionStorage.removeItem(SESSION_HINT_KEY); } catch (_) {}
    authCache.pendingMfaAccessToken = null;
    set({ user: null, isAuthenticated: false, isLoading: false, session: null, isSuspended: false, pendingMfaEmail: null });
  }
};

export const signInWithGoogle = async (set: any) => {
  set({ isLoading: true, error: null });
  console.log('🔵 Starting Google OAuth...');

  try {
    const currentOrigin = window.location.origin;
    const redirectTo = `${currentOrigin}${ROUTES.LOGIN}`;
    console.log('🔗 Redirect to:', redirectTo);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        skipBrowserRedirect: false,
      },
    });

    if (error) {
      console.error('❌ Google OAuth error:', error.message);
      const authError = handleAuthError(error);
      set({ error: authError.message, isLoading: false });
      throw authError;
    }
    console.log('✅ Google OAuth initiated');
  } catch (error: any) {
    console.error('❌ Google sign-in failed:', error);
    const authError = handleAuthError(error);
    set({ error: authError.message, isLoading: false });
    throw authError;
  }
};

export const resetPassword = async (email: string, set: any) => {
  set({ isLoading: true, error: null });
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}${ROUTES.RESET_PASSWORD}`,
    });

    if (error) {
      const authError = handleAuthError(error);
      set({ error: authError.message, isLoading: false });
      throw authError;
    }

    set({ isLoading: false });
    console.log('✅ Password reset email sent');
  } catch (error: any) {
    console.error('❌ Password reset failed:', error);
    const authError = handleAuthError(error);
    set({ error: authError.message, isLoading: false });
    throw authError;
  }
};

export const resendVerificationEmail = async (email: string, set: any) => {
  set({ isLoading: true, error: null });
  try {
    const redirectUrl = buildVerificationRedirectUrl();
    let { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });

    if (error?.message?.toLowerCase().includes('redirect') || error?.message?.toLowerCase().includes('redirect_to')) {
      console.warn('⚠️ Resend failed due to redirect URL policy, retrying without emailRedirectTo');
      ({ error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      }));
    }

    if (error) {
      const authError = handleAuthError(error);
      set({ error: authError.message, isLoading: false });
      throw authError;
    }

    set({ isLoading: false });
    console.log('✅ Verification email sent');
  } catch (error: any) {
    console.error('❌ Failed to resend verification email:', error);
    const authError = handleAuthError(error);
    set({ error: authError.message, isLoading: false });
    throw authError;
  }
};

export const fetchUserProfile = async (set: any, get: any) => {
  const { user, session } = get();
  if (!user || !session) return;

  try {
    const fullUser = await fetchUserProfileFromDB(user.id);
    if (fullUser) {
      const currentUser = get().user;
      const hasChanged = !currentUser || 
        currentUser.plan !== fullUser.plan ||
        currentUser.email !== fullUser.email ||
        currentUser.username !== fullUser.username ||
        currentUser.storageUsed !== fullUser.storageUsed ||
        currentUser.storageLimit !== fullUser.storageLimit ||
        currentUser.isSuspended !== fullUser.isSuspended;
      
      if (hasChanged) {
        console.log('✅ User profile refreshed (values changed)');
        set({ user: fullUser, isSuspended: fullUser.isSuspended || false });
        authCache.userStateCache = fullUser;
        if (typeof window !== 'undefined') {
          try {
            sessionStorage.setItem(USER_STATE_STORAGE_KEY, JSON.stringify(fullUser));
          } catch (e) {}
        }
      } else {
        console.log('📦 User profile unchanged - skipping update to prevent flicker');
      }
    }
  } catch (error) {
    console.error('❌ Failed to refresh user profile:', error);
  }
};

export const updateUser = async (updates: Partial<AuthUser>, set: any, get: any) => {
  try {
    const { user } = get();
    if (!user) return;

    const profileUpdates: Record<string, any> = {};
    if (typeof updates.username === 'string') {
      profileUpdates.full_name = updates.username;
    }

    if (Object.keys(profileUpdates).length > 0) {
      const { error } = await supabase.from('profiles').update(profileUpdates).eq('id', user.id);
      if (error) throw error;
    }

    set({ user: { ...user, ...updates }, isSuspended: (updates.isSuspended !== undefined ? updates.isSuspended : user.isSuspended) || false });
    console.log('✅ User profile updated');
  } catch (error) {
    console.error('❌ Failed to update user:', error);
    set({ error: 'Failed to update user' });
  }
};

export const checkSuspended = async (set: any, get: any): Promise<boolean> => {
  try {
    const { user } = get();
    if (!user) return false;

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('is_suspended')
      .eq('id', user.id)
      .single();

    if (error || !profile) {
      console.warn('⚠️ Could not check suspension status:', error);
      return false;
    }

    const isSuspended = profile.is_suspended === true;
    
    if (isSuspended) {
      console.warn('⚠️ Account is suspended - logging out immediately');
      authCache.sessionCache = { session: null, timestamp: Date.now() };
      
      const suspensionChannel = (get() as any)._suspensionChannel;
      if (suspensionChannel) {
        supabase.removeChannel(suspensionChannel);
        (get() as any)._suspensionChannel = null;
        authCache.suspensionListenerSetup = false;
      }
      
      await supabase.auth.signOut();
      set({ 
        user: null, 
        isAuthenticated: false, 
        isLoading: false,
        session: null,
        error: 'ACCOUNT_SUSPENDED',
        isSuspended: true
      });
      
      import('@utils/sentry').then(({ clearUser }) => {
        clearUser();
      }).catch(() => {});
      
      return true;
    }

    return false;
  } catch (error) {
    console.error('❌ Error checking suspension status:', error);
    return false;
  }
};
